/*
 * The funnel's ribbons: pure geometry (no DOM, no d3), so it can be tested. A ribbon runs BEHIND the page boxes, from the edge of one step's box to the edge of the next, as thick as the
 * visitors it carries and getting narrower as fewer are left: the narrowing IS the loss. Design of record: docs/FUNNEL_STEP_INDICATORS.md ("CURRENT DIRECTION").
 *
 *  - width: the square root of the visitors, so a step that keeps a tenth of its visitors is about a third as wide, not a tenth (a tenth would vanish), in proportion to the box's height
 *    (boxes scale with the screen);
 *  - each ribbon leaves the box edge that FACES the next box and arrives at the edge that faces back, flat-ended and square to the edge, like a pipe joining two boxes;
 *  - two ribbons using the same edge of one box are spread along it, in the order that keeps them from crossing, so they never pile onto one point.
 */
export interface Box {
  /** identifies the box (the page): two ribbons that touch the same box share an edge only if they share this */
  key: string;
  /** centre, in the grid's own coordinates */
  x: number;
  y: number;
  w: number;
  h: number;
}

export type Side = "l" | "r" | "t" | "b";

/** A ribbon's widest and narrowest, as fractions of the box's height. */
export const RIBBON_MAX = 0.82;
export const RIBBON_MIN = 0.07;

export const ribbonWidth = (visitors: number, maxVisitors: number, boxH: number): number => {
  if (!(maxVisitors > 0)) return RIBBON_MIN * boxH;

  return Math.max(RIBBON_MIN * boxH, RIBBON_MAX * boxH * Math.sqrt(Math.min(1, Math.max(0, visitors) / maxVisitors)));
};

/** Which edge of each box faces the other. Mostly sideways goes out of the side edges; mostly up or down goes out of the top and bottom. */
export const facingSides = (a: Box, b: Box): { from: Side; to: Side } => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) * 0.65 >= Math.abs(dy)) return dx >= 0 ? { from: "r", to: "l" } : { from: "l", to: "r" };

  return dy >= 0 ? { from: "b", to: "t" } : { from: "t", to: "b" };
};

/**
 * The flow ribbons' choice of edges: boxes in the SAME ROW join across their side edges, boxes in DIFFERENT ROWS join bottom-to-top - out of the bottom of the upper box, into the top of the lower
 * one - however far apart sideways they are (Chris, 2026-10-19: "utilizing the TOPS and BOTTOMS of the page slot boxes for the ribbons to connect into"). On a five-column, three-row grid the
 * facing rule above sends nearly every route out of a SIDE (the columns are further apart than the rows), so a box's two side edges carried a dozen ribbons each while its top and bottom,
 * which face the other rows, carried none.
 */
export const rowAwareSides = (a: Box, b: Box): { from: Side; to: Side } => {
  const dy = b.y - a.y;
  if (Math.abs(dy) >= 0.6 * Math.min(a.h, b.h)) return dy >= 0 ? { from: "b", to: "t" } : { from: "t", to: "b" };

  return b.x - a.x >= 0 ? { from: "r", to: "l" } : { from: "l", to: "r" };
};

export const OUT: Record<Side, [number, number]> = { r: [1, 0], l: [-1, 0], b: [0, 1], t: [0, -1] };

/** A point on a box's edge: at the edge's middle plus `offset` along the edge. */
export const edgePoint = (box: Box, side: Side, offset = 0): { x: number; y: number } => {
  if (side === "r") return { x: box.x + box.w / 2, y: box.y + offset };
  if (side === "l") return { x: box.x - box.w / 2, y: box.y + offset };
  if (side === "b") return { x: box.x + offset, y: box.y + box.h / 2 };

  return { x: box.x + offset, y: box.y - box.h / 2 };
};

export interface RibbonSpec {
  id: string;
  from: Box;
  to: Box;
  /** which edge it leaves and which it arrives at, when the geometry should not decide (the flow ribbons' spawn point always sends right); absent = the edges that face each other */
  sides?: { from: Side; to: Side };
  /** how far the ribbon runs straight out of each edge before it bends, in pixels; absent = 45% of the distance (at least 20). A short handle keeps a ribbon between two close edges from bulging out past them. */
  handle?: number;
  /** flow ribbons: how far from the first edge, along the way the ribbon leaves, its sideways run sits (absent: half way). The spawn's arrivals pass a short one so they climb the gutter beside the spawn bar. */
  mid?: number;
  /** width where it leaves the first box, and where it arrives at the second */
  startW: number;
  endW: number;
}

/** One sample along a ribbon's centre line: the point, the unit normal there, and half the ribbon's width there. */
export interface CentreSample {
  x: number;
  y: number;
  nx: number;
  ny: number;
  half: number;
}

export interface RibbonShape {
  /** how it was routed: a "pipe" (straight runs and round corners) or the old "curve" (one cubic); for tests and for measuring how often the curve is still drawn */
  route?: "pipe" | "curve";
  id: string;
  /** an SVG path, closed, in the grid's coordinates */
  d: string;
  /** the centre line, from the first box's edge to the second's: what a visitor riding the ribbon follows */
  centre: CentreSample[];
  startW: number;
  endW: number;
  /** where it meets the first box's edge and the second box's edge (after any spreading along the edge) */
  start: { x: number; y: number };
  end: { x: number; y: number };
}

/** How far the ribbon reaches past the box edge it joins. 0: the ribbon's end is FLUSH with the box's edge (Chris, 2026-10-23: the ends went over the page box and into it; it was 3 px, to hide a gap in the box's border). */
const INSET = 0;
const SAMPLES = 40;

const smooth = (p: number) => p * p * (3 - 2 * p);

export interface Outline {
  left: Array<[number, number]>;
  right: Array<[number, number]>;
  centre: CentreSample[];
}

/** The two sides of a ribbon from one edge point to another, leaving square to the first edge and arriving square to the second, narrowing from startW to endW. */
export const ribbonOutline = (
  p0: { x: number; y: number },
  out0: [number, number],
  p3: { x: number; y: number },
  out3: [number, number],
  startW: number,
  endW: number,
  n = SAMPLES,
  handle?: number
): Outline => {
  const dist = Math.hypot(p3.x - p0.x, p3.y - p0.y);
  const h = handle ?? Math.max(20, 0.45 * dist);
  const a = { x: p0.x - out0[0] * INSET, y: p0.y - out0[1] * INSET };
  const d = { x: p3.x - out3[0] * INSET, y: p3.y - out3[1] * INSET };
  const b = { x: p0.x + out0[0] * h, y: p0.y + out0[1] * h };
  const c = { x: p3.x + out3[0] * h, y: p3.y + out3[1] * h };
  const left: Array<[number, number]> = [];
  const right: Array<[number, number]> = [];
  const centre: CentreSample[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const u = 1 - t;
    const x = u * u * u * a.x + 3 * u * u * t * b.x + 3 * u * t * t * c.x + t * t * t * d.x;
    const y = u * u * u * a.y + 3 * u * u * t * b.y + 3 * u * t * t * c.y + t * t * t * d.y;
    const dx = 3 * u * u * (b.x - a.x) + 6 * u * t * (c.x - b.x) + 3 * t * t * (d.x - c.x);
    const dy = 3 * u * u * (b.y - a.y) + 6 * u * t * (c.y - b.y) + 3 * t * t * (d.y - c.y);
    const len = Math.hypot(dx, dy) || 1;
    const half = (startW + (endW - startW) * smooth(t)) / 2;
    left.push([x - (dy / len) * half, y + (dx / len) * half]);
    right.push([x + (dy / len) * half, y - (dx / len) * half]);
    centre.push({ x, y, nx: -dy / len, ny: dx / len, half });
  }

  return { left, right, centre };
};

/*
 * A RIBBON BETWEEN TWO ROWS (2026-10-19). Chris, on seeing a flow between a box on one row and a box three columns along on the next: "Some of these ribbon connections look awful."
 *
 * The ribbon leaves the bottom of one box (or the top) and arrives square at the top of the other, in the channel between the rows. That channel is short (the gap between the rows) and the
 * ribbon may be hundreds of pixels long sideways. One cubic curve with a handle no longer than half the gap (the handle that keeps it in the channel) turns almost on the spot at each end:
 * measured for a 66 px gap, an 800 px run and a 46 px ribbon the curve's radius at the end is about 2 px, far under the ribbon's half-width of 23, so the inner edge of the ribbon folds over
 * itself and the end is a notch. A ribbon's outline is its centre line pushed out by half its width on each side: wherever the centre line bends tighter than that, the outline is wrong.
 *
 * So the centre line is built for the channel instead: straight out of the edge, a quarter circle that uses HALF the gap, straight along the middle of the channel, a quarter circle, straight in.
 * Each bend has a radius of half the gap, which stays clear of the ribbon's half-width for every ribbon the gap can hold. When it cannot be built (the ribbon is too wide for the gap, or the two
 * boxes are too close sideways for two quarter circles) it is not used, and the cubic, which is fine for a short run, stays.
 */
/** A centre line as samples (position and the direction it is heading) pushed out by half the width on each side. The width eases from startW to endW by the distance travelled. */
const outlineOf = (pts: Array<{ x: number; y: number; tx: number; ty: number }>, startW: number, endW: number): Outline => {
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  const total = cum[cum.length - 1] || 1;
  const left: Array<[number, number]> = [];
  const right: Array<[number, number]> = [];
  const centre: CentreSample[] = [];
  pts.forEach((c, i) => {
    const w = (startW + (endW - startW) * smooth(cum[i] / total)) / 2;
    const nx = -c.ty;
    const ny = c.tx;
    left.push([c.x + nx * w, c.y + ny * w]);
    right.push([c.x - nx * w, c.y - ny * w]);
    centre.push({ x: c.x, y: c.y, nx, ny, half: w });
  });

  return { left, right, centre };
};

const ARC_SAMPLES = 14;
const RUN_SAMPLES = 12;
const STRAIGHT_SAMPLES = 6;
/** the bend radius must leave this much of the centre line's radius beyond the ribbon's half-width */
const BEND_CLEARANCE = 3;

/**
 * The channel route, in a frame of its own: AXIAL is the way the ribbon leaves the first edge (and arrives at the second), LATERAL is the way it has to shift sideways to line up. Straight out for
 * `mid - R`, a quarter circle of radius R, straight along the lateral run, a quarter circle, straight in. `mid` is how far along the axial distance the sideways run sits: half way for the channel
 * between two rows (the middle of the channel), and near the start for a ribbon leaving the spawn bar (up or down the gutter beside the bar, then along the row to its page, not diagonally across
 * the grid). R is as large as the room allows (the axial room on either side of the run, and half the lateral run) and must clear the ribbon's half-width, or the route is not built.
 */
export const channelOutline = (
  p0: { x: number; y: number },
  out0: [number, number],
  p3: { x: number; y: number },
  out3: [number, number],
  startW: number,
  endW: number,
  mid?: number
): Outline | null => {
  // the ribbon leaves along out0 and must arrive travelling the same way (so into an edge whose outward direction is the opposite): a bottom to a top, a right to a left
  if (out0[0] !== -out3[0] || out0[1] !== -out3[1] || out0[0] * out0[1] !== 0) return null;
  const ax = out0[0];
  const ay = out0[1];
  const dx = p3.x - p0.x;
  const dy = p3.y - p0.y;
  const G = dx * ax + dy * ay; // the axial distance: positive when the second box lies the way the ribbon leaves
  const lateralRaw = dx * -ay + dy * ax; // along the perpendicular (bx, by) = (-ay, ax)
  const sign = lateralRaw < 0 ? -1 : 1;
  const bx = -ay * sign;
  const by = ax * sign;
  const Lat = Math.abs(lateralRaw);
  if (!(G > 0) || !(Lat > 0)) return null;
  const M = Math.max(0, Math.min(G, mid ?? G / 2));
  const R = Math.min(M, G - M, Lat / 2);
  const half = Math.max(startW, endW) / 2;
  if (R < half + BEND_CLEARANCE) return null;

  // the centre line, sample by sample, each with the direction it is heading, in (axial, lateral) first
  const q: Array<{ u: number; v: number; tu: number; tv: number }> = [];
  const straight = (u0: number, u1: number, v: number) => {
    if (u1 - u0 < 1e-6) return;
    for (let i = 0; i <= STRAIGHT_SAMPLES; i++) q.push({ u: u0 + ((u1 - u0) * i) / STRAIGHT_SAMPLES, v, tu: 1, tv: 0 });
  };
  q.push({ u: -INSET, v: 0, tu: 1, tv: 0 });
  straight(0, M - R, 0);
  for (let i = 0; i <= ARC_SAMPLES; i++) {
    const th = (i / ARC_SAMPLES) * (Math.PI / 2);
    q.push({ u: M - R + R * Math.sin(th), v: R - R * Math.cos(th), tu: Math.cos(th), tv: Math.sin(th) });
  }
  for (let i = 1; i < RUN_SAMPLES; i++) q.push({ u: M, v: R + ((Lat - 2 * R) * i) / RUN_SAMPLES, tu: 0, tv: 1 });
  for (let i = 0; i <= ARC_SAMPLES; i++) {
    const ph = (i / ARC_SAMPLES) * (Math.PI / 2);
    q.push({ u: M + R - R * Math.cos(ph), v: Lat - R + R * Math.sin(ph), tu: Math.sin(ph), tv: Math.cos(ph) });
  }
  straight(M + R, G, Lat);
  q.push({ u: G + INSET, v: Lat, tu: 1, tv: 0 });

  // (where a straight piece meets a bend both emit the same point: a repeated point is a step of no length, which has no direction and would only confuse whatever walks the outline)
  const pts = q
    .map((c) => ({ x: p0.x + ax * c.u + bx * c.v, y: p0.y + ay * c.u + by * c.v, tx: ax * c.tu + bx * c.tv, ty: ay * c.tu + by * c.tv }))
    .filter((c, i, all) => i === 0 || Math.hypot(c.x - all[i - 1].x, c.y - all[i - 1].y) > 1e-9);

  return outlineOf(pts, startW, endW);
};

/*
 * EVERY RIBBON IS A PIPE (2026-10-19). Chris, comparing the demo with staging: "I don't like the non-straight pipe version here like how you have it on demo. Demo being straight pipes is what I want to see
 * in the app please." The demo's flows run between boxes whose edges face each other, which channelOutline already makes into pipes; staging's busier flows also join a BOTTOM to a SIDE, or two
 * BOTTOMS, and those were still one long cubic that swung across the grid in a big arc.
 *
 * A pipe has straight runs and round corners only, and every join between two edges is one of three shapes, by how the way it LEAVES the first edge (d0) compares with the way it ARRIVES at the second (t3,
 * the opposite of that edge's outward direction):
 *   - the same way (a bottom to a top, a right to a left): an S (channelOutline);
 *   - at right angles (a bottom to a side): an L, one corner;
 *   - back the way it came (a bottom to a bottom): a U, out past both boxes, across, and back.
 * Each is a polyline with every corner rounded by the same radius R, which is as big as the room allows (up to ROUND_MAX) and must clear the ribbon's half-width, or the pipe is not built and the old curve
 * is used (only where the geometry leaves no room: a target behind the edge it leaves, a ribbon too wide for its corners).
 */
const ROUND_MAX = 90;
/** how far a U runs out past the further of its two boxes, beyond its corner radius */
const U_MARGIN = 8;

/** A polyline with every corner (all of them right angles) rounded by `r`, as samples that carry the direction they are heading. Segments must be at least `r` long at the ends and `2r` between two corners. */
const roundedRoute = (way: Array<{ x: number; y: number }>, r: number): Array<{ x: number; y: number; tx: number; ty: number }> => {
  const dirs = way.slice(1).map((p, i) => {
    const dx = p.x - way[i].x;
    const dy = p.y - way[i].y;
    const len = Math.hypot(dx, dy) || 1;

    return { x: dx / len, y: dy / len };
  });
  const out: Array<{ x: number; y: number; tx: number; ty: number }> = [];
  const straight = (a: { x: number; y: number }, b: { x: number; y: number }, d: { x: number; y: number }) => {
    for (let i = 0; i <= STRAIGHT_SAMPLES; i++) out.push({ x: a.x + ((b.x - a.x) * i) / STRAIGHT_SAMPLES, y: a.y + ((b.y - a.y) * i) / STRAIGHT_SAMPLES, tx: d.x, ty: d.y });
  };
  for (let k = 0; k < dirs.length; k++) {
    const d = dirs[k];
    const from = k === 0 ? way[0] : { x: way[k].x + d.x * r, y: way[k].y + d.y * r };
    const last = k === dirs.length - 1;
    const to = last ? way[way.length - 1] : { x: way[k + 1].x - d.x * r, y: way[k + 1].y - d.y * r };
    straight(from, to, d);
    if (!last) {
      // the corner at way[k + 1]: an arc from `to` (heading d) round to heading `n`; its centre is `to` + n * r
      const n = dirs[k + 1];
      for (let i = 1; i <= ARC_SAMPLES; i++) {
        const th = (i / ARC_SAMPLES) * (Math.PI / 2);
        out.push({
          x: to.x + n.x * r + (-n.x * Math.cos(th) + d.x * Math.sin(th)) * r,
          y: to.y + n.y * r + (-n.y * Math.cos(th) + d.y * Math.sin(th)) * r,
          tx: n.x * Math.sin(th) + d.x * Math.cos(th),
          ty: n.y * Math.sin(th) + d.y * Math.cos(th),
        });
      }
    }
  }

  return out;
};

/** The corner radius that fits a polyline of right angles: no bigger than `cap`, no bigger than its end segments, no bigger than half of each segment between two corners. */
const fitRadius = (way: Array<{ x: number; y: number }>, cap: number): number => {
  const lens = way.slice(1).map((p, i) => Math.hypot(p.x - way[i].x, p.y - way[i].y));
  let r = cap;
  lens.forEach((len, i) => {
    const ends = (i === 0 ? 1 : 0) + (i === lens.length - 1 ? 1 : 0);
    r = Math.min(r, ends === 0 ? len / 2 : len);
  });

  return r;
};

/** The outline of a pipe through `way` with the largest corner radius that fits (null when it cannot clear the ribbon). */
const pipeThrough = (p0: { x: number; y: number }, out0: [number, number], p3: { x: number; y: number }, out3: [number, number], way: Array<{ x: number; y: number }>, startW: number, endW: number): Outline | null => {
  const r = fitRadius(way, ROUND_MAX);
  if (!(r >= Math.max(startW, endW) / 2 + BEND_CLEARANCE)) return null;
  const route = roundedRoute(way, r);
  // tucked under the box at each end, square to its edge
  const pts = [{ x: p0.x - out0[0] * INSET, y: p0.y - out0[1] * INSET, tx: out0[0], ty: out0[1] }, ...route, { x: p3.x - out3[0] * INSET, y: p3.y - out3[1] * INSET, tx: -out3[0], ty: -out3[1] }].filter(
    (c, i, all) => i === 0 || Math.hypot(c.x - all[i - 1].x, c.y - all[i - 1].y) > 1e-9
  );

  return outlineOf(pts, startW, endW);
};

export const pipeOutline = (
  p0: { x: number; y: number },
  out0: [number, number],
  p3: { x: number; y: number },
  out3: [number, number],
  startW: number,
  endW: number,
  mid?: number
): Outline | null => {
  if (out0[0] * out0[1] !== 0 || out3[0] * out3[1] !== 0 || (out0[0] === 0 && out0[1] === 0) || (out3[0] === 0 && out3[1] === 0)) return null;
  const t3: [number, number] = [-out3[0], -out3[1]]; // the way it is travelling as it arrives
  const dx = p3.x - p0.x;
  const dy = p3.y - p0.y;
  const along = (d: [number, number]) => dx * d[0] + dy * d[1];
  const dot = out0[0] * t3[0] + out0[1] * t3[1];
  const at = (u: number, d: [number, number], v = 0, e: [number, number] = [0, 0]) => ({ x: p0.x + d[0] * u + e[0] * v, y: p0.y + d[1] * u + e[1] * v });

  if (dot === 1) return channelOutline(p0, out0, p3, out3, startW, endW, mid); // an S
  if (dot === 0) {
    // an L: out along d0 as far as the second box, then along t3 into it
    const a = along(out0);
    const b = along(t3);
    if (a > 0 && b > 0) return pipeThrough(p0, out0, p3, out3, [p0, at(a, out0), p3], startW, endW);

    // The second box's edge cannot be reached with one corner: it is behind the way the ribbon leaves (a <= 0), or it must be entered from a side the first corner would not reach (b <= 0): the
    // ribbon would have to turn BEFORE the target and come back. So a short lead out of the first edge, a drop into the room beside the target's edge, a run along it, and a turn into the edge: a
    // pipe of three corners (Z) with the smallest corner radius that clears the ribbon (a bend this size needs no more room than a ribbon is wide).
    const r = Math.max(Math.max(startW, endW) / 2 + BEND_CLEARANCE + 6, 24);
    const x1 = Math.abs(a - r) >= 2 * r ? r : a + 2 * r; // the lead along d0: far enough from the run's end for two corners
    let y1 = b - r - U_MARGIN; // the level of the run: just short of the target's edge, so the last turn is a short step into it
    if (Math.abs(y1) < 2 * r) y1 = y1 >= 2 * r ? y1 : -2 * r;
    const corner = (x: number, y: number) => ({ x: p0.x + out0[0] * x + t3[0] * y, y: p0.y + out0[1] * x + t3[1] * y });

    return pipeThrough(p0, out0, p3, out3, [p0, corner(x1, 0), corner(x1, y1), corner(a, y1), p3], startW, endW);
  }
  // a U: out along d0 past both boxes, across, and back along t3
  const perp: [number, number] = [-out0[1], out0[0]];
  const lateral = along(perp);
  const lat = Math.abs(lateral);
  if (!(lat > 0)) return null;
  const e: [number, number] = lateral < 0 ? [-perp[0], -perp[1]] : perp;
  const half = Math.max(startW, endW) / 2 + BEND_CLEARANCE;
  const r = Math.min(lat / 2, ROUND_MAX);
  if (r < half) return null;
  const u3 = along(out0);
  const depth = Math.max(0, u3) + r + U_MARGIN;

  return pipeThrough(p0, out0, p3, out3, [p0, at(depth, out0), at(depth, out0, lat, e), p3], startW, endW);
};

const fmt = (p: [number, number]) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`;
export const outlinePath = (o: Outline): string => `M${o.left.map(fmt).join("L")}L${o.right.slice().reverse().map(fmt).join("L")}Z`;

export interface LayoutOptions {
  /**
   * Use the TOPS and BOTTOMS of the boxes (rowAwareSides: different rows join bottom-to-top) and keep every ribbon's end ON the edge it joins: when an edge is too short for all the ribbons that want it (sum of their widths), the ones that can face a neighbouring edge instead
   * WRAP AROUND to it - up and over to the top, or round to the bottom, of the box - and the rest are packed along the edge rather than spilling past its corner. Used by the flow ribbons,
   * where a box can have a dozen routes into it (Chris, 2026-10-19: "Some page slots to other page slots don't line up... wrap the end of the ribbons around so it connects to the tops or
   * bottoms of the page slot boxes?"). The funnel's ribbons (one in, one out) lay out exactly as before.
   */
  wrap?: boolean;
  /**
   * Whether a ribbon may be moved ONTO this edge of this box (wrap only). An edge with nothing beside it - the top of the top row, the bottom of the bottom row, the outer edge of the last column -
   * has no channel to run in: a ribbon wrapped onto it has to loop out past the edge of the canvas and back (Chris, 2026-10-19: "Some of these ribbon connections look awful").
   * Absent: every edge is open.
   */
  edgeOpen?: (boxKey: string, side: Side) => boolean;
}

/** The space left between two ribbon ends on one edge, in pixels. */
const EDGE_GAP = 2;
/** the edges a ribbon can wrap round to from each edge: the two at right angles to it */
const NEIGHBOURS: Record<Side, [Side, Side]> = { l: ["t", "b"], r: ["t", "b"], t: ["l", "r"], b: ["l", "r"] };

/** How much a side points at where the other box is (1 = straight at it, 0 = along it, -1 = away). */
const facing = (side: Side, dx: number, dy: number): number => (OUT[side][0] * dx + OUT[side][1] * dy) / (Math.hypot(dx, dy) || 1);

/** Lays every ribbon out, spreading the ones that share an edge of a box. */
export const layoutRibbons = (specs: RibbonSpec[], options: LayoutOptions = {}): RibbonShape[] => {
  interface Port {
    spec: number;
    end: 0 | 1;
    boxKey: string;
    side: Side;
    /** where the OTHER end is, along the edge's own axis: orders the ports so ribbons do not cross */
    other: number;
    len: number;
    /** the ribbon's width where it meets this edge */
    width: number;
    /** the box's size, and the direction of the box at the other end (to judge which other edge a ribbon could wrap round to) */
    bw: number;
    bh: number;
    dx: number;
    dy: number;
    /** the geometry must not decide this end's edge (the flow ribbons' spawn point always sends right) */
    fixed: boolean;
  }
  const sides = specs.map((s) => s.sides ?? (options.wrap ? rowAwareSides(s.from, s.to) : facingSides(s.from, s.to)));
  const makePort = (i: number, end: 0 | 1, side: Side): Port => {
    const s = specs[i];
    const box = end === 0 ? s.from : s.to;
    const partner = end === 0 ? s.to : s.from;
    const alongY = side === "l" || side === "r";

    return {
      spec: i,
      end,
      boxKey: box.key,
      side,
      other: alongY ? partner.y : partner.x,
      len: alongY ? box.h : box.w,
      width: end === 0 ? s.startW : s.endW,
      bw: box.w,
      bh: box.h,
      dx: partner.x - box.x,
      dy: partner.y - box.y,
      fixed: !!s.sides,
    };
  };
  // (const, changed in place: the build's Babel Flow-strip step failed on a reassigned `let x: T[]` before)
  const ports: Port[] = [];
  specs.forEach((_, i) => {
    ports.push(makePort(i, 0, sides[i].from));
    ports.push(makePort(i, 1, sides[i].to));
  });
  const groupsOf = (list: Port[]) => {
    const groups = new Map<string, Port[]>();
    list.forEach((p) => {
      const k = `${p.boxKey}|${p.side}`;
      groups.set(k, [...(groups.get(k) || []), p]);
    });

    return groups;
  };
  /** how full an edge is: the ribbons' widths and the gaps between them, as a share of its length (above 1 they do not fit) */
  const fill = (g: Port[], extra?: Port): number => {
    const all = extra ? [...g, extra] : g;
    if (!all.length) return 0;

    return (all.reduce((sum, p) => sum + p.width, 0) + EDGE_GAP * (all.length - 1)) / all[0].len;
  };

  if (options.wrap) {
    const stuck = new Set<string>();
    for (let guard = 0; guard < ports.length * 3; guard++) {
      const groups = groupsOf(ports);
      let fullKey: string | null = null;
      let worst = 1.0001;
      groups.forEach((g, k) => {
        const r = fill(g);
        if (!stuck.has(k) && r > worst) {
          worst = r;
          fullKey = k;
        }
      });
      if (fullKey === null) break;
      const full = groups.get(fullKey) as Port[];
      let best: { port: Port; side: Side; resulting: number; facing: number } | null = null;
      for (const p of full) {
        if (p.fixed) continue;
        for (const side of NEIGHBOURS[p.side]) {
          if (options.edgeOpen && !options.edgeOpen(p.boxKey, side)) continue; // nothing beside that edge to run in
          const point = facing(side, p.dx, p.dy);
          if (point < -0.05) continue; // never round to an edge that points away from where it is going
          const moved = makePort(p.spec, p.end, side);
          const resulting = fill(groups.get(`${p.boxKey}|${side}`) || [], moved);
          if (resulting >= worst) continue; // the other edge has to be emptier than this one, or nothing is gained
          const fits = resulting <= 1;
          const better =
            !best ||
            (fits && best.resulting > 1) || // a move that fits beats one that does not
            (fits && best.resulting <= 1 && point > best.facing) || // among those that fit, the one pointing most at where the ribbon is going
            (!fits && best.resulting > 1 && resulting < best.resulting); // among those that do not fit, the emptiest edge
          if (better) best = { port: p, side, resulting, facing: point };
        }
      }
      if (!best) {
        stuck.add(fullKey);
        continue;
      }
      const chosen = best as { port: Port; side: Side };
      ports[ports.indexOf(chosen.port)] = makePort(chosen.port.spec, chosen.port.end, chosen.side);
    }
    ports.forEach((p) => {
      if (p.end === 0) sides[p.spec] = { ...sides[p.spec], from: p.side };
      else sides[p.spec] = { ...sides[p.spec], to: p.side };
    });
  }

  const offsets = new Map<string, number>();
  groupsOf(ports).forEach((g) => {
    g.sort((a, b) => a.other - b.other || a.spec - b.spec || a.end - b.end);
    const n = g.length;
    const len = g[0].len;
    const usual = g.map((_, i) => (i - (n - 1) / 2) * 0.3 * len);
    let placed = usual;
    // wrapping layouts keep every band ON the edge: where the usual spacing would spill past the corner, pack the ribbons along it (equal gaps when they fit, evenly overlapping when they cannot)
    if (options.wrap && !g.every((p, i) => Math.abs(usual[i]) + p.width / 2 <= len / 2 + 0.5)) {
      const total = g.reduce((sum, p) => sum + p.width, 0);
      if (total + EDGE_GAP * (n - 1) <= len) {
        const gap = (len - total) / (n + 1);
        let at = -len / 2 + gap;
        placed = g.map((p) => {
          const centre = at + p.width / 2;
          at += p.width + gap;

          return centre;
        });
      } else {
        const mean = total / n;
        placed = g.map((p, i) => Math.max(-(len - p.width) / 2, Math.min((len - p.width) / 2, (n > 1 ? i / (n - 1) - 0.5 : 0) * (len - mean))));
      }
    }
    g.forEach((p, i) => offsets.set(`${p.spec}:${p.end}`, placed[i]));
  });

  return specs.map((s, i) => {
    const p0 = edgePoint(s.from, sides[i].from, offsets.get(`${i}:0`) || 0);
    const p3 = edgePoint(s.to, sides[i].to, offsets.get(`${i}:1`) || 0);

    // A ribbon that joins a bottom to a top (or the reverse) runs in the channel between two rows: a handle of the usual length (45% of the distance, which is mostly SIDEWAYS here) would swing it
    // out of the channel in a huge loop, so it is limited to half the vertical gap (the arrivals' handle does the same job for the gap at the grid's left edge).
    const vertical = (sides[i].from === "t" || sides[i].from === "b") && (sides[i].to === "t" || sides[i].to === "b");
    const handle = s.handle ?? (options.wrap && vertical ? Math.max(8, Math.min(0.45 * Math.hypot(p3.x - p0.x, p3.y - p0.y), 0.5 * Math.abs(p3.y - p0.y))) : undefined);
    // every ribbon is a pipe wherever one fits (see pipeOutline); the old curve is only for geometry with no room for a pipe
    const pipe = options.wrap ? pipeOutline(p0, OUT[sides[i].from], p3, OUT[sides[i].to], s.startW, s.endW, s.mid) : null;
    const outline = pipe ?? ribbonOutline(p0, OUT[sides[i].from], p3, OUT[sides[i].to], s.startW, s.endW, SAMPLES, handle);

    return { route: pipe ? "pipe" : "curve", id: s.id, d: outlinePath(outline), centre: outline.centre, startW: s.startW, endW: s.endW, start: p0, end: p3 } as RibbonShape;
  });
};
